import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar'; // Assuming you use `react-calendar`
import Header from '../Components/Header';
import TrainingService from '../../Service/TrainingService';
import TrainingModel from '../../Model/TrainingModel';
import 'react-calendar/dist/Calendar.css';
import "./Loader.css";
import { Helmet } from 'react-helmet';

const CalendarView = ({ session, loading }) => {
    const [value, setValue] = useState(new Date()); // Date sélectionnée
    const [showPopup, setShowPopup] = useState(false); // Contrôle de la visibilité du popup
    const [selectedFormations, setSelectedFormations] = useState([]); // Stockage des formations sélectionnées
    const [loadingDetails, setLoadingDetails] = useState(false); // État de chargement pour les détails des formations

    // Fonction utilitaire pour convertir une chaîne de date en objet `Date`
    const parseDate = (dateStr) => {
        const parsedDate = new Date(dateStr); // Assure une conversion en temps local
        return isNaN(parsedDate) ? null : parsedDate;
    };

    const today = new Date();

    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6; // Dimanche (0) ou Samedi (6)
    };

    const isOnlyWeekendFormation = (startDate, endDate) => {
        const startDay = startDate.getDay();
        const endDay = endDate.getDay();
        return (startDay === 0 || startDay === 6) && (endDay === 0 || endDay === 6);
    };

    const LoadingOverlay = () => (
        <div className="loading-overlay">
            <div className="spinner"></div>
        </div>
    );

    const formatDateToLocal = (date) => {
        return date.toLocaleDateString('en-CA'); // Format "YYYY-MM-DD" en fuseau local
    };
    // Récupérer les détails pour une session spécifique via son ID
    const fetchSessionDetails = async (sessionId) => {
        try {
            let response = await TrainingService.fetchSessionsById(sessionId);
            let training = new TrainingModel();
            training.name = response.data.training_session.name;
            training.dateDebut = response.data.training_session.start_date;
            training.dateFin = response.data.training_session.end_date;
            training.id = response.data.training_session.id;
            training.secondaryId = response.data.training_session.training.original_training_id;
            return training;
        } catch (error) {
            console.error("Erreur lors de la récupération des détails :", error);
            return null;
        }
    };

    // Gestion du clic sur une tile du calendrier
    const handleTileClick = async (date) => {
        const tileDateString = formatDateToLocal(date);

        const matchingFormations = session.filter(f => {
            const startDate = parseDate(f.dateDebut);
            const endDate = parseDate(f.dateFin);

            if (startDate && endDate) {
                const startDateString = formatDateToLocal(startDate);
                const endDateString = formatDateToLocal(endDate);

                return tileDateString >= startDateString && tileDateString <= endDateString;
            }
            return false;
        });

        // Exclure les formations qui commencent par "devis"
        const filteredFormations = matchingFormations.filter(formation =>
            !formation.name.toLowerCase().startsWith("devis")
        );

        if (filteredFormations.length > 0) {
            setLoadingDetails(true); // Commencer le chargement

            // Récupérer les détails supplémentaires pour chaque session
            const formationDetailsPromises = filteredFormations.map(formation =>
                fetchSessionDetails(formation.id)
            );

            const formationDetails = await Promise.all(formationDetailsPromises);
            setSelectedFormations(formationDetails.filter(Boolean)); // Exclure les échecs
            setShowPopup(true); // Afficher le popup

            setLoadingDetails(false); // Fin du chargement
        }
    };

    // Contenu des tiles du calendrier
    const getTileContent = ({ date, view }) => {
        if (view === 'month' && !loading) {
            const tileDateString = formatDateToLocal(date);

            const matchingFormations = session.filter(f => {
                const startDate = parseDate(f.dateDebut);
                const endDate = parseDate(f.dateFin);

                if (startDate && endDate) {
                    const isExtended = startDate < endDate;
                    const startDateString = formatDateToLocal(startDate);
                    const endDateString = formatDateToLocal(endDate);

                    // Si la formation est étendue, exclure les week-ends sauf si c'est uniquement le week-end
                    if (isExtended && !isOnlyWeekendFormation(startDate, endDate) && isWeekend(date)) {
                        return false;
                    }

                    return tileDateString >= startDateString && tileDateString <= endDateString;
                }
                return false;
            });

            const filteredFormations = matchingFormations.filter(formation =>
                !formation.name.toLowerCase().startsWith("devis")
            );

            if (filteredFormations.length > 0) {
                return (
                    <div
                        className="formation-names"
                        style={{ color: 'blue', border: '1px solid black', cursor: 'pointer' }}
                    >
                        {filteredFormations.map((formation, index) => (
                            <div key={index}>{formation.name}</div>
                        ))}
                    </div>
                );
            }
        }

        return null;
    };

    // Composant Popup pour afficher les détails des formations
    const Popup = ({ formations, onClose }) => (
        <div className="popup-overlay">
            <div className="popup-content">
                <h3>Formations</h3>
                {loadingDetails ? (
                    <div>Loading...</div>
                ) : (
                    <ul>
                        {formations.map((formation, index) => (
                            <li key={index}>
                                <a href={`/formation/${formation.secondaryId}`}>
                                    {formation.name}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
                <button onClick={onClose}>Fermer</button>
            </div>
        </div>
    );

    // Rendu principal du composant
    return (
        <>
            <Helmet>
                <title>Calendrier</title>
                <meta name="description" content="Calendrier des formations" />
                <meta name="robots" content="index, follow" />
            </Helmet>
            <Header title="CALENDRIER" />
            {loading && <LoadingOverlay />}
            <div>
                <div className="calendar-container">
                    <Calendar
                        onChange={setValue}
                        value={value}
                        tileContent={getTileContent}
                        onClickDay={handleTileClick}
                        minDate={today} // Empêche d'aller avant le mois actuel
                    />
                </div>
                {showPopup && (
                    <Popup
                        formations={selectedFormations}
                        onClose={() => setShowPopup(false)}
                    />
                )}
            </div>
        </>
    );
};

export default CalendarView;